import { useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };

const uid = () => Math.random().toString(36).slice(2, 10);

// Éditeur compact d'options produit (option_groups). Réutilise le contrat menu_pricing :
// groupe {name, min_select, max_select, choices:[{name, price_delta, is_available, track_stock, stock_qty}]}.
export const PartnerOptionsEditor = ({ item, onClose, onSave }) => {
  const [groups, setGroups] = useState(() =>
    (item.option_groups || []).map((g) => ({
      id: g.id || uid(), name: g.name || "", min_select: g.min_select ?? 0,
      max_select: g.max_select ?? 1,
      choices: (g.choices || []).map((c) => ({
        id: c.id || uid(), name: c.name || "", price_delta: c.price_delta ?? 0,
        is_available: c.is_available !== false, track_stock: !!c.track_stock,
        stock_qty: c.stock_qty ?? 0,
      })),
    }))
  );
  const [busy, setBusy] = useState(false);

  const patchGroup = (gi, p) => setGroups((gs) => gs.map((g, i) => (i === gi ? { ...g, ...p } : g)));
  const patchChoice = (gi, ci, p) =>
    setGroups((gs) => gs.map((g, i) => i === gi
      ? { ...g, choices: g.choices.map((c, j) => (j === ci ? { ...c, ...p } : c)) } : g));
  const addGroup = () => setGroups((gs) => [...gs, { id: uid(), name: "", min_select: 0, max_select: 1, choices: [{ id: uid(), name: "", price_delta: 0, is_available: true, track_stock: false, stock_qty: 0 }] }]);
  const removeGroup = (gi) => setGroups((gs) => gs.filter((_, i) => i !== gi));
  const addChoice = (gi) => setGroups((gs) => gs.map((g, i) => i === gi ? { ...g, choices: [...g.choices, { id: uid(), name: "", price_delta: 0, is_available: true, track_stock: false, stock_qty: 0 }] } : g));
  const removeChoice = (gi, ci) => setGroups((gs) => gs.map((g, i) => i === gi ? { ...g, choices: g.choices.filter((_, j) => j !== ci) } : g));

  const save = async () => {
    const clean = groups
      .map((g) => ({
        id: g.id, name: g.name.trim(), min_select: Number(g.min_select) || 0,
        max_select: g.max_select === "" || g.max_select == null ? null : Number(g.max_select),
        choices: g.choices
          .filter((c) => c.name.trim())
          .map((c) => ({
            id: c.id, name: c.name.trim(), price_delta: Math.max(0, parseFloat(String(c.price_delta).replace(",", ".")) || 0),
            is_available: !!c.is_available, track_stock: !!c.track_stock,
            stock_qty: c.track_stock ? Math.max(0, parseInt(c.stock_qty, 10) || 0) : null,
          })),
      }))
      .filter((g) => g.name && g.choices.length > 0);
    setBusy(true);
    try { await onSave(clean); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(2,6,23,.55)" }} data-testid="opt-editor-overlay">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[88vh] overflow-y-auto" data-testid="opt-editor">
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#eef0ef" }}>
          <div>
            <p className="text-xs font-bold uppercase" style={{ color: "#94a3b8" }}>Options & suppléments</p>
            <p className="font-extrabold" style={{ color: GREEN }}>{item.name}</p>
          </div>
          <button data-testid="opt-editor-close" onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#f1f5f9" }}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {groups.length === 0 && <p className="text-sm text-center" style={{ color: "#94a3b8" }}>Aucune option. Ajoutez un groupe (ex : Cuisson, Sauces, Suppléments).</p>}
          {groups.map((g, gi) => (
            <div key={g.id} data-testid={`opt-group-${gi}`} className="rounded-2xl p-3" style={{ border: "1px solid #eef0ef" }}>
              <div className="flex items-center gap-2 mb-2">
                <input data-testid={`opt-group-name-${gi}`} value={g.name} onChange={(e) => patchGroup(gi, { name: e.target.value })}
                  placeholder="Nom du groupe (ex : Cuisson)" className="flex-1 rounded-xl px-3 h-10 text-sm outline-none font-semibold" style={field} />
                <button data-testid={`opt-group-del-${gi}`} onClick={() => removeGroup(gi)} className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#fef2f2", color: "#dc2626" }}><Trash2 size={16} /></button>
              </div>
              <div className="flex items-center gap-2 mb-3 text-xs" style={{ color: "#64748b" }}>
                <span>min</span>
                <input data-testid={`opt-group-min-${gi}`} type="number" min="0" value={g.min_select} onChange={(e) => patchGroup(gi, { min_select: e.target.value })} className="w-14 rounded-lg px-2 h-8 outline-none" style={field} />
                <span>max</span>
                <input data-testid={`opt-group-max-${gi}`} type="number" min="1" value={g.max_select ?? ""} onChange={(e) => patchGroup(gi, { max_select: e.target.value })} placeholder="∞" className="w-14 rounded-lg px-2 h-8 outline-none" style={field} />
                <span className="ml-1">{Number(g.min_select) > 0 ? "obligatoire" : "facultatif"}</span>
              </div>
              {g.choices.map((c, ci) => (
                <div key={c.id} data-testid={`opt-choice-${gi}-${ci}`} className="flex flex-wrap items-center gap-2 py-1.5 border-t" style={{ borderColor: "#f5f7f6" }}>
                  <input data-testid={`opt-choice-name-${gi}-${ci}`} value={c.name} onChange={(e) => patchChoice(gi, ci, { name: e.target.value })} placeholder="Choix" className="flex-1 min-w-[110px] rounded-lg px-2 h-9 text-sm outline-none" style={field} />
                  <div className="flex items-center gap-1">
                    <span className="text-xs" style={{ color: "#94a3b8" }}>+CHF</span>
                    <input data-testid={`opt-choice-price-${gi}-${ci}`} value={c.price_delta} onChange={(e) => patchChoice(gi, ci, { price_delta: e.target.value })} inputMode="decimal" className="w-16 rounded-lg px-2 h-9 text-sm outline-none" style={field} />
                  </div>
                  <label className="flex items-center gap-1 text-xs" style={{ color: "#64748b" }}>
                    <input type="checkbox" data-testid={`opt-choice-track-${gi}-${ci}`} checked={c.track_stock} onChange={(e) => patchChoice(gi, ci, { track_stock: e.target.checked })} />stock
                  </label>
                  {c.track_stock && (
                    <input data-testid={`opt-choice-qty-${gi}-${ci}`} type="number" min="0" value={c.stock_qty} onChange={(e) => patchChoice(gi, ci, { stock_qty: e.target.value })} className="w-14 rounded-lg px-2 h-9 text-sm outline-none" style={field} />
                  )}
                  <label className="flex items-center gap-1 text-xs" style={{ color: "#64748b" }}>
                    <input type="checkbox" data-testid={`opt-choice-avail-${gi}-${ci}`} checked={c.is_available} onChange={(e) => patchChoice(gi, ci, { is_available: e.target.checked })} />dispo
                  </label>
                  <button data-testid={`opt-choice-del-${gi}-${ci}`} onClick={() => removeChoice(gi, ci)} className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ color: "#dc2626" }}><X size={15} /></button>
                </div>
              ))}
              <button data-testid={`opt-add-choice-${gi}`} onClick={() => addChoice(gi)} className="mt-2 text-xs font-bold flex items-center gap-1" style={{ color: ORANGE }}><Plus size={14} /> Ajouter un choix</button>
            </div>
          ))}
          <button data-testid="opt-add-group" onClick={addGroup} className="w-full rounded-xl py-2.5 text-sm font-bold flex items-center justify-center gap-1" style={{ border: `1.5px dashed ${GREEN}`, color: GREEN }}><Plus size={16} /> Ajouter un groupe d&apos;options</button>
        </div>

        <div className="sticky bottom-0 bg-white flex gap-2 px-5 py-4 border-t" style={{ borderColor: "#eef0ef" }}>
          <button data-testid="opt-editor-cancel" onClick={onClose} className="flex-1 rounded-xl font-bold h-11" style={{ background: "#f1f5f9", color: "#334155" }}>Annuler</button>
          <button data-testid="opt-editor-save" onClick={save} disabled={busy} className="flex-1 rounded-xl font-bold text-white h-11" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>{busy ? "…" : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
};
